import React, { useState, useEffect, useRef } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import { eventGroupsOnWeb } from '@lib/feature-flags'

const SearchComponent = () => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const searchRef = useRef(null)
  const inputRef = useRef(null)

  // Detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Fetch search results
  const shouldFetch = query.length >= 2
  const { data: searchResults, isLoading } = useSWR(
    shouldFetch ? `/search.json?q=${encodeURIComponent(query)}${eventGroupsOnWeb() ? '&group=1' : ''}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 300
    }
  )

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        setQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Use requestAnimationFrame for better reliability on mobile
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }
  }, [isOpen])

  const handleSearchClick = () => {
    if (isMobile) {
      setIsOpen(true)
    }
  }

  const handleInputChange = (e) => {
    setQuery(e.target.value)
    if (!isOpen && !isMobile) {
      setIsOpen(true)
    }
  }

  const handleResultClick = (url) => {
    window.location.href = url
  }

  const getTypeLabel = (type) => {
    if (type === 'event_group') return 'Series'

    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  const getTypeStyle = (type) => {
    switch (type) {
      case 'event':
      case 'event_group':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'article':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'page':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  // Mobile overlay
  if (isMobile && isOpen) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder="Search events, articles, pages..."
            className="flex-grow px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="off"
            autoFocus
          />
        </div>

        {/* Results */}
        <div className="flex-grow flex flex-col min-h-0">
          {query.length < 2 ? (
            <div className="text-gray-600 dark:text-gray-400 text-center mt-8 px-4">
              Enter at least 2 characters to search
            </div>
          ) : isLoading ? (
            <div className="text-gray-600 dark:text-gray-400 text-center mt-8 px-4">
              Searching...
            </div>
          ) : searchResults?.results?.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-400 text-center mt-8 px-4">
              No results found for "{query}"
            </div>
          ) : searchResults?.results && searchResults.results.length > 0 ? (
            <>
              {/* Sticky "View all results" link at top */}
              <div className="sticky top-0 bg-blue-50 dark:bg-gray-800 border-b border-blue-200 dark:border-gray-700 px-4 py-3 z-10">
                <a
                  href={`/search?q=${encodeURIComponent(query)}`}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium flex items-center justify-between"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.location.href = `/search?q=${encodeURIComponent(query)}`
                  }}
                >
                  <span>View all results for "{query}"</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
              
              {/* Scrollable results */}
              <div className="flex-grow overflow-y-auto px-4 pb-4">
                <div className="space-y-3 mt-3">
                  {searchResults.results.map((result, index) => (
                    <div
                      key={index}
                      onClick={() => handleResultClick(result.url)}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex gap-3">
                        {result.image_url && (
                          <img
                            src={result.image_url}
                            alt={result.title}
                            className="w-16 h-16 object-cover rounded flex-shrink-0"
                            loading="lazy"
                          />
                        )}
                        <div className="flex-grow min-w-0">
                          <div className="mb-1 flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeStyle(result.type)}`}>
                              {getTypeLabel(result.type)}
                            </span>
                            {result.last_updated && (result.type === 'article' || result.type === 'page') && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Updated {result.last_updated}
                              </span>
                            )}
                            {result.event_dates && (result.type === 'event' || result.type === 'event_group') && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {result.event_dates}
                              </span>
                            )}
                          </div>
                          <h3 
                            className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2 mb-1"
                            dangerouslySetInnerHTML={{ __html: result.title }}
                          />
                          <p 
                            className="text-gray-600 dark:text-gray-400 text-xs line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: result.content }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div ref={searchRef} className="relative">
      {/* Search Input/Icon */}
      {isMobile ? (
        <button
          onClick={handleSearchClick}
          className="p-1.5 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder="Search..."
            className="w-64 px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="off"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      )}

      {/* Desktop Results Dropdown */}
      {!isMobile && isOpen && (
        <div className="absolute top-full mt-2 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
          {query.length < 2 ? (
            <div className="p-4 text-gray-600 dark:text-gray-400 text-sm">
              Enter at least 2 characters to search
            </div>
          ) : isLoading ? (
            <div className="p-4 text-gray-600 dark:text-gray-400 text-sm">
              Searching...
            </div>
          ) : searchResults?.results?.length === 0 ? (
            <div className="p-4 text-gray-600 dark:text-gray-400 text-sm">
              No results found for "{query}"
            </div>
          ) : (
            <div className="py-2">
              {searchResults?.results?.map((result, index) => (
                <div
                  key={index}
                  onClick={() => handleResultClick(result.url)}
                  className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className="flex gap-3">
                    {result.image_url && (
                      <img
                        src={result.image_url}
                        alt={result.title}
                        className="w-12 h-12 object-cover rounded flex-shrink-0"
                        loading="lazy"
                      />
                    )}
                    <div className="flex-grow min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeStyle(result.type)}`}>
                          {getTypeLabel(result.type)}
                        </span>
                        {result.last_updated && (result.type === 'article' || result.type === 'page') && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Updated {result.last_updated}
                          </span>
                        )}
                        {result.event_dates && (result.type === 'event' || result.type === 'event_group') && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {result.event_dates}
                          </span>
                        )}
                      </div>
                      <h4 
                        className="font-medium text-gray-900 dark:text-white text-sm line-clamp-1 mb-1"
                        dangerouslySetInnerHTML={{ __html: result.title }}
                      />
                      <p 
                        className="text-gray-600 dark:text-gray-400 text-xs line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: result.content }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* View all results link */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                <a
                  href={`/search?q=${encodeURIComponent(query)}`}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
                >
                  View all results for "{query}"
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchComponent